import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  BadgeCheck,
  ChevronsUpDown,
  Ellipsis,
  Home,
  Plus,
  ScanLine,
  Search,
  Sparkles,
  Timer,
  X,
} from "lucide-react";
import { IonPage, IonHeader, IonToolbar, IonContent } from "@ionic/react";
import { useStore } from "../store/useStore";
import { useChatStore } from "../store/useChatStore";
import {
  countCompletedToday,
  countDueTasks,
  describeAreaSummary,
  pluralizeTasks,
  summarizeArea,
} from "../logic/home-stats";
import { Avatar } from "../components/ui/Avatar";
import { IconButton } from "../components/ui/IconButton";
import { StatCard } from "../components/ui/StatCard";
import { SpaceRow } from "../components/ui/SpaceRow";
import { Skeleton, SkeletonList } from "../components/ui/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { HouseholdSwitcherSheet } from "../components/household/HouseholdSwitcherSheet";
import type { SwitcherView } from "../components/household/HouseholdSwitcherSheet";

/** How many spaces to show before "Show more". */
const COLLAPSED_SPACE_COUNT = 4;
const AI_PROMO_DISMISSED_KEY = "tidy:home:aiPromoDismissed";

const readPromoDismissed = (): boolean => {
  try {
    return window.localStorage.getItem(AI_PROMO_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
};

const writePromoDismissed = () => {
  try {
    window.localStorage.setItem(AI_PROMO_DISMISSED_KEY, "1");
  } catch {
    // Storage unavailable — the card is still hidden for this session.
  }
};

// ---------- AI promo card ----------

function AiPromoCard({ onOpen, onDismiss }: { onOpen: () => void; onDismiss: () => void }) {
  return (
    <div className="relative overflow-hidden rounded-3xl border-2 border-dashed border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50 dark:border-orange-400/25 dark:from-orange-500/10 dark:via-amber-500/5 dark:to-rose-500/10">
      {/* Decorative shapes */}
      <div
        aria-hidden="true"
        className="absolute -bottom-6 -right-4 w-32 h-24 rotate-12 rounded-3xl bg-gradient-to-br from-orange-300 to-rose-300 opacity-70 dark:opacity-40"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-10 right-16 w-20 h-20 rotate-45 rounded-2xl bg-amber-200 opacity-70 dark:opacity-30"
      />
      <IconButton
        label="Dismiss AI setup suggestion"
        icon={<X className="w-4 h-4" />}
        onClick={onDismiss}
        className="absolute top-1 right-1 text-muted-foreground"
      />
      <button
        type="button"
        onClick={onOpen}
        className="relative w-full text-left p-5 pr-14 rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-orange-700 dark:text-orange-300 mb-1">
          <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
          Tidy AI
        </span>
        <span className="block text-lg font-extrabold leading-snug text-foreground max-w-[15rem]">
          Generate AI tasks and spaces
        </span>
        <span className="block mt-1 text-sm text-muted-foreground max-w-[14rem]">
          Tell Tidy about your home and we'll set up rooms and tasks for you.
        </span>
      </button>
    </div>
  );
}

// ---------- Main page ----------

export default function HomeStatusPage() {
  const history = useHistory();
  const areas = useStore((s) => s.areas);
  const tasks = useStore((s) => s.tasks);
  const taskCompletions = useStore((s) => s.taskCompletions);
  const cleanlinessMap = useStore((s) => s.cleanlinessMap);
  const loading = useStore((s) => s.loading);
  const households = useStore((s) => s.households);
  const currentHouseholdId = useStore((s) => s.currentHouseholdId);
  const openChat = useChatStore((s) => s.open);
  const setChatMode = useChatStore((s) => s.setChatMode);

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [switcherView, setSwitcherView] = useState<SwitcherView>("list");
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [promoDismissed, setPromoDismissed] = useState(readPromoDismissed);

  const currentHousehold = households.find((h) => h.uuid === currentHouseholdId);
  const householdName = currentHousehold?.name ?? "My Home";

  const completedToday = useMemo(
    () => countCompletedToday(taskCompletions),
    [taskCompletions]
  );
  const dueCount = useMemo(
    () => countDueTasks(tasks, cleanlinessMap),
    [tasks, cleanlinessMap]
  );

  // Spaces sorted so the ones needing the most attention come first
  const spaces = useMemo(() => {
    return areas
      .map((area) => ({ area, summary: summarizeArea(area.uuid, tasks, cleanlinessMap) }))
      .sort((a, b) => {
        if (a.summary.taskCount === 0 && b.summary.taskCount > 0) return 1;
        if (b.summary.taskCount === 0 && a.summary.taskCount > 0) return -1;
        return b.summary.avgDirty - a.summary.avgDirty;
      });
  }, [areas, tasks, cleanlinessMap]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredSpaces = normalizedQuery
    ? spaces.filter(({ area }) => area.name.toLowerCase().includes(normalizedQuery))
    : spaces;
  const showAll = expanded || normalizedQuery.length > 0;
  const visibleSpaces = showAll
    ? filteredSpaces
    : filteredSpaces.slice(0, COLLAPSED_SPACE_COUNT);
  const hiddenCount = filteredSpaces.length - visibleSpaces.length;

  const openSwitcher = (view: SwitcherView) => {
    setSwitcherView(view);
    setSwitcherOpen(true);
  };

  const openAiSetup = () => {
    setChatMode("text");
    openChat();
  };

  const dismissPromo = () => {
    writePromoDismissed();
    setPromoDismissed(true);
  };

  const toggleSearch = () => {
    setSearchOpen((open) => {
      if (open) setQuery("");
      return !open;
    });
  };

  const isLoading = loading.areas || loading.tasks;

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <div className="max-w-lg mx-auto w-full px-3 pt-2 pb-1 flex items-center gap-1">
            <button
              type="button"
              onClick={() => openSwitcher("list")}
              aria-haspopup="dialog"
              aria-label={`Current household: ${householdName}. Switch household`}
              className="flex items-center gap-2.5 min-w-0 min-h-[44px] rounded-2xl pl-1 pr-2 py-1 hover:bg-surface transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Avatar
                name={householdName}
                seed={currentHouseholdId}
                shape="square"
                size="md"
              />
              <span className="text-xl font-extrabold tracking-tight text-foreground truncate">
                {householdName}
              </span>
              <ChevronsUpDown
                className="w-4 h-4 text-muted-foreground shrink-0"
                aria-hidden="true"
              />
            </button>
            <div className="flex-1" />
            <IconButton
              label={searchOpen ? "Close search" : "Search spaces"}
              aria-pressed={searchOpen}
              icon={<Search className="w-[22px] h-[22px]" />}
              onClick={toggleSearch}
            />
            <IconButton
              label="Join a household with an invite code"
              icon={<ScanLine className="w-[22px] h-[22px]" />}
              onClick={() => openSwitcher("join")}
            />
          </div>
          {searchOpen && (
            <div className="max-w-lg mx-auto w-full px-4 pb-2">
              <input
                type="search"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search spaces…"
                aria-label="Search spaces"
                className="input-field min-h-[44px] py-2"
              />
            </div>
          )}
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <h1 className="sr-only">Home</h1>
        <div className="max-w-lg mx-auto px-4 pt-2 pb-28">
          {/* Stat cards */}
          <div className="grid grid-cols-2 gap-3 mb-7">
            {isLoading ? (
              <>
                <Skeleton className="h-32 rounded-3xl" />
                <Skeleton className="h-32 rounded-3xl" />
              </>
            ) : (
              <>
                <StatCard
                  tone="cyan"
                  icon={<BadgeCheck className="w-5 h-5" />}
                  value={completedToday}
                  title="Completed"
                  subtitle={`${pluralizeTasks(completedToday)} done today`}
                  onClick={() => history.push("/today")}
                />
                <StatCard
                  tone="purple"
                  icon={<Timer className="w-5 h-5" />}
                  value={dueCount}
                  title="Due today"
                  subtitle={
                    dueCount === 0 ? "All caught up!" : `${pluralizeTasks(dueCount)} need attention`
                  }
                  onClick={() => history.push("/today")}
                />
              </>
            )}
          </div>

          {/* My Spaces */}
          <section aria-labelledby="my-spaces-heading" className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 id="my-spaces-heading" className="section-title text-xl">
                My Spaces
              </h2>
              <div className="flex items-center -mr-2">
                <IconButton
                  label="Set up spaces with Tidy AI"
                  icon={<Sparkles className="w-5 h-5 text-brand-purple" />}
                  onClick={openAiSetup}
                />
                <IconButton
                  label="Add a space"
                  icon={<Plus className="w-[22px] h-[22px]" />}
                  onClick={() => history.push("/areas/create")}
                />
                <IconButton
                  label="Manage spaces"
                  icon={<Ellipsis className="w-[22px] h-[22px]" />}
                  onClick={() => history.push("/settings/areas")}
                />
              </div>
            </div>

            {isLoading ? (
              <SkeletonList rows={3} />
            ) : areas.length === 0 ? (
              <EmptyState
                icon={Home}
                title="No spaces yet"
                description="Add the rooms you want to keep tidy, or let Tidy AI set them up for you."
                action={{ label: "Add a space", onClick: () => history.push("/areas/create") }}
              />
            ) : filteredSpaces.length === 0 ? (
              <p className="py-6 text-center text-muted-foreground">
                No spaces match “{query.trim()}”.
              </p>
            ) : (
              <ul className="space-y-0.5">
                {visibleSpaces.map(({ area, summary }) => (
                  <li key={area.uuid}>
                    <SpaceRow
                      name={area.name}
                      subtitle={describeAreaSummary(summary)}
                      highlightSubtitle={summary.dueCount > 0}
                      dirtyPercent={summary.taskCount > 0 ? summary.avgDirty : undefined}
                      onClick={() => history.push(`/areas/${area.uuid}?mode=view`)}
                    />
                  </li>
                ))}
              </ul>
            )}

            {!isLoading && !normalizedQuery && filteredSpaces.length > COLLAPSED_SPACE_COUNT && (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                aria-expanded={expanded}
                className="mt-3 w-full min-h-[48px] rounded-full bg-surface hover:bg-surface-raised text-foreground font-semibold inline-flex items-center justify-center gap-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {expanded ? (
                  "Show fewer spaces"
                ) : (
                  <>
                    <Plus className="w-4 h-4" aria-hidden="true" />
                    Show more spaces ({hiddenCount})
                  </>
                )}
              </button>
            )}
          </section>

          {!promoDismissed && <AiPromoCard onOpen={openAiSetup} onDismiss={dismissPromo} />}
        </div>
      </IonContent>

      <HouseholdSwitcherSheet
        isOpen={switcherOpen}
        initialView={switcherView}
        onDismiss={() => setSwitcherOpen(false)}
      />
    </IonPage>
  );
}
